import { SharedCard, SharedSwitch } from '@components';
import { DemoMode, TenantProfileService } from '@services';
import { Button, Col, InputNumber, Row, Space, Typography, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

const { Text, Title } = Typography;

const DEFAULT_ALLOWANCE = 25;

interface IProps {
  tenantId: number;
}

/**
 * Host-only control for putting a garage on a trial and topping up its vehicle lookups.
 *
 * Switching the trial off is the whole of converting it to a paying account — no data is
 * migrated or removed, and any unspent lookups stay with them.
 */
export const DemoAccountCard: React.FC<IProps> = ({ tenantId }) => {
  const [demoMode, setDemoMode] = useState<DemoMode | null>(null);
  const [lookups, setLookups] = useState<number>(DEFAULT_ALLOWANCE);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await TenantProfileService.getDemoMode(tenantId);
      setDemoMode(result);
      // Seed from the balance they actually hold, so the field never shows a number that
      // would silently change it. A tenant taken off trial keeps its unspent lookups, and
      // without this, putting it back on trial would quietly cut them to the default.
      // Falls back to the default only when there is genuinely nothing to preserve.
      setLookups(result.lookupBalance > 0 ? result.lookupBalance : DEFAULT_ALLOWANCE);
    } catch {
      // The http interceptor already surfaces the failure; swallow it here so a rejected
      // promise in an effect does not take the rest of the tenant page down with it.
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (isDemo: boolean, lookupBalance: number) => {
      setSaving(true);
      try {
        // The write returns the resulting state, so there is no second round trip to
        // wait through before the switch reflects the change.
        const result = await TenantProfileService.setDemoMode({
          tenantId,
          isDemo,
          lookupBalance,
        });
        setDemoMode(result);
        setLookups(result.lookupBalance);
        message.success({
          content: isDemo ? 'Trial account enabled' : 'Trial account disabled — full access',
        });
      } catch {
        // Surfaced by the http interceptor. Caught here because these handlers are wired
        // straight to onClick/onChange, where a rejected promise goes unhandled.
      } finally {
        setSaving(false);
      }
    },
    [tenantId]
  );

  const handleToggle = useCallback(
    (isDemo: boolean) => save(isDemo, lookups),
    [save, lookups]
  );

  const handleSetLookups = useCallback(() => save(true, lookups), [save, lookups]);

  if (!demoMode) return null;

  return (
    <SharedCard style={{ boxShadow: 'none' }}>
      <Title level={5}>Trial account</Title>

      <Row align="middle" gutter={[16, 16]}>
        <Col>
          <Space>
            <SharedSwitch
              active={demoMode.isDemo}
              loading={saving}
              onChange={() => handleToggle(!demoMode.isDemo)}
            />
            <Text>{demoMode.isDemo ? 'On trial' : 'Full account'}</Text>
          </Space>
        </Col>

        {demoMode.isDemo && (
          <Col>
            <Space>
              <Text type={demoMode.lookupBalance > 0 ? 'secondary' : 'danger'}>
                Vehicle lookups
              </Text>
              <InputNumber
                min={0}
                value={lookups}
                onChange={(value) => setLookups(value ?? 0)}
                disabled={saving}
              />
              <Button
                onClick={handleSetLookups}
                loading={saving}
                disabled={lookups === demoMode.lookupBalance}
              >
                Save
              </Button>
            </Space>
          </Col>
        )}
      </Row>

      <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
        On a trial, Chat, Reports and Online booking setup are marked &quot;Paid only&quot;, as is
        anything that sends email, takes a card payment, connects accounting or reads an invoice
        with AI. Turning it off unlocks everything and keeps all their data.
      </Text>
    </SharedCard>
  );
};
